import React, { useState } from 'react';
import { config } from '../../config';

const TestimonyCard = ({ name, text, index }: { name: string; text: string; index: number }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const maxLength = 150;

  const shouldTruncate = text.length > maxLength;
  const displayText = isExpanded || !shouldTruncate ? text : text.slice(0, maxLength) + '...';

  return (
    <div 
      className={`p-8 rounded-[2rem] bg-white border border-secondary/5 shadow-sm hover:shadow-xl transition-all duration-500 group ${
        index % 2 === 0 ? 'lg:translate-y-8' : ''
      }`}
    >
      <div className="mb-6">
        <svg className="w-10 h-10 text-primary/20 group-hover:text-primary transition-colors duration-500" fill="currentColor" viewBox="0 0 24 24">
          <path d="M14.017 21L14.017 18C14.017 16.8954 14.9124 16 16.017 16H19.017C19.5693 16 20.017 15.5523 20.017 15V9C20.017 8.44772 19.5693 8 19.017 8H15.017C14.4647 8 14.017 8.44772 14.017 9V11C14.017 11.5523 13.5693 12 13.017 12H12.017V5H22.017V15C22.017 18.3137 19.3307 21 16.017 21H14.017ZM5.0166 21L5.0166 18C5.0166 16.8954 5.91203 16 7.0166 16H10.0166C10.5689 16 11.0166 15.5523 11.0166 15V9C11.0166 8.44772 10.5689 8 10.0166 8H6.0166C5.46432 8 5.0166 8.44772 5.0166 9V11C5.0166 11.5523 4.56889 12 4.0166 12H3.0166V5H13.0166V15C13.0166 18.3137 10.3303 21 7.0166 21H5.0166Z" />
        </svg>
      </div>
      <p className="text-secondary/70 italic mb-6 leading-relaxed text-lg">"{displayText}"</p>
      {shouldTruncate && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-primary font-bold text-xs tracking-widest uppercase mb-6 hover:text-primary-dark transition-colors"
        >
          {isExpanded ? 'Read Less' : 'Read More'}
        </button>
      )}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center text-primary font-bold border border-primary/10">
          {name.charAt(0)}
        </div>
        <div>
          <p className="font-bold text-secondary">{name}</p>
          <p className="text-[10px] text-secondary/40 uppercase tracking-[0.2em]">Member</p>
        </div>
      </div>
    </div>
  );
};

export const Testimonies = () => {
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    if (formData.get('_gotcha')) {
      return;
    }

    try {
      setFormStatus('submitting');
      const response = await fetch(config.formspree_endpoint, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to submit testimony');
      }

      setFormStatus('success');
      form.reset();
    } catch {
      setFormStatus('error');
    }
  };

  const testimonies = [
    {
      name: "Sis Chelsea",
      text: "I want to thank God for His faithfulness in my life. I was going through a very difficult season where everything seemed to be working against me. My health was failing, and I was facing financial challenges. But I held on to God's word and the prayers of the church. One Sunday, during the worship service, I felt a sudden peace wash over me. From that day on, things started to turn around. My health improved, and God provided a financial breakthrough that I never expected. I am a living testimony that God answers prayers!"
    },
    {
      name: "Sarah M.",
      text: "I was going through a difficult time in my career, but after attending the prayer meetings, God opened a door I never expected. Truly faithful!"
    },
    {
      name: "David K.",
      text: "The community here has been a blessing to my family. We've found a home where we can grow in faith and love."
    },
    {
      name: "Grace T.",
      text: "Healing came to my body after the pastor prayed for me. I am a living testimony of God's miraculous power."
    }
  ];

  return (
    <section id="testimonies" className="section-padding bg-white">
      <div className="container-custom">
        <div className="text-center mb-24">
          <p className="text-primary font-bold tracking-[0.3em] uppercase text-xs mb-6">Stories of Faith</p>
          <h2 className="text-4xl sm:text-6xl font-bold mb-8">Testimonies</h2>
          <p className="text-lg text-secondary/60 max-w-2xl mx-auto">
            Be encouraged by what God is doing in the lives of our church family.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-12 mb-32">
          {testimonies.map((testimony, index) => (
            <TestimonyCard key={index} {...testimony} index={index} />
          ))}
        </div>

        {/* Share Your Testimony CTA */}
        <div className="max-w-4xl mx-auto">
          {!isFormVisible ? (
            <div className="glass-panel p-12 sm:p-16 rounded-[3rem] text-center border border-primary/10">
              <h3 className="text-3xl font-bold mb-6">Has God done something amazing?</h3>
              <p className="text-secondary/60 mb-10 max-w-xl mx-auto">
                Your testimony can encourage others and build their faith. We invite you to share your story with us.
              </p>
              <button
                onClick={() => setIsFormVisible(true)}
                className="btn-primary"
              >
                Share Your Story
              </button>
            </div>
          ) : (
            <div id="testimony-form" className="bg-accent p-10 sm:p-16 rounded-[3rem] shadow-2xl border border-secondary/5 animate-fade-in-up">
              <div className="flex justify-between items-center mb-12">
                <h3 className="text-3xl font-bold">Share Your Story</h3>
                <button onClick={() => setIsFormVisible(false)} className="p-2 hover:text-primary transition-colors">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {formStatus === 'success' ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-8">
                    <svg className="w-10 h-10 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h4 className="text-2xl font-bold mb-4">Thank You!</h4>
                  <p className="text-secondary/60 mb-8">Your testimony has been submitted for review.</p>
                  <button onClick={() => { setFormStatus('idle'); setIsFormVisible(false); }} className="text-primary font-bold underline">Close</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-8">
                  <input type="text" name="_gotcha" className="hidden" tabIndex={-1} autoComplete="off" />
                  <input type="hidden" name="_subject" value="New Testimony Submission" />
                  <input type="hidden" name="source" value="Testimonies Form" />
                  <div className="grid sm:grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase tracking-widest text-secondary/40">Full Name</label>
                      <input name="name" type="text" required className="w-full bg-white px-6 py-4 rounded-2xl border border-secondary/5 focus:border-primary outline-none transition-all" placeholder="Your name" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase tracking-widest text-secondary/40">Email</label>
                      <input name="email" type="email" className="w-full bg-white px-6 py-4 rounded-2xl border border-secondary/5 focus:border-primary outline-none transition-all" placeholder="your@email.com" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-widest text-secondary/40">Your Testimony</label>
                    <textarea name="message" required rows={6} className="w-full bg-white px-6 py-4 rounded-2xl border border-secondary/5 focus:border-primary outline-none transition-all resize-none" placeholder="What has God done in your life?"></textarea>
                  </div>
                  <div className="flex items-center gap-3">
                    <input name="permission" value="yes" type="checkbox" required id="permission" className="w-5 h-5 rounded-lg border-secondary/20 text-primary focus:ring-primary" />
                    <label htmlFor="permission" className="text-sm text-secondary/60">I give permission to share this testimony.</label>
                  </div>
                  {formStatus === 'error' && (
                    <p role="alert" className="text-sm text-red-600">Unable to submit right now. Please try again.</p>
                  )}
                  <button type="submit" disabled={formStatus === 'submitting'} className="btn-primary w-full">
                    {formStatus === 'submitting' ? 'Submitting...' : 'Submit Testimony'}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
